import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { equal } from '@ember/object/computed';
import ApiService from 'cio/services/api';
import AccountService from 'cio/services/account';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import { restartableTask } from 'ember-concurrency';
import SetupGuide from 'cio/models/setup-guide';
import EnvironmentService from 'cio/services/environment';

type VerificationRequest = {
  website: string;
  description: string;
  technicalContact: string;
};

type VerificationRequirements = {
  data: boolean;
  channel: boolean;
  message: boolean;
};

type VerificationRequirement = keyof VerificationRequirements;

export default class SetupGuideSendCampaignController extends Controller {
  @service account!: AccountService;
  @service api!: ApiService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;

  model!: SetupGuide;

  @computed('model.{firstMessageCreated,anyMessagingChannelComplete}', 'fetchIntegrationStatusTask.last.value')
  get verificationRequirements(): VerificationRequirements {
    let { customers } = this.fetchIntegrationStatusTask.last?.value ?? { customers: 0 };

    return {
      data: customers > 0,
      channel: this.model.anyMessagingChannelComplete,
      message: this.model.firstMessageCreated,
    };
  }

  @computed('verificationRequirements.{data,channel,message}')
  get pendingVerificationRequirements() {
    return (Object.keys(this.verificationRequirements) as VerificationRequirement[]).filter(
      (key: VerificationRequirement) => !this.verificationRequirements[key],
    );
  }

  @equal('pendingVerificationRequirements.length', 0) canVerifyAccount!: boolean;

  @computed('verificationRequirements.{data,channel,message}')
  get lastPendingVerificationRequirement() {
    let [requirement] = this.pendingVerificationRequirements;

    return {
      data: {
        text: 'added one or more customers',
        routeName: 'setup-guide.sources',
      },
      message: {
        text: 'created a campaign',
        routeName: 'setup-guide.create-campaign',
      },
      channel: {
        text: 'set up a messaging channel',
        routeName: 'setup-guide.channels',
      },
    }[requirement];
  }

  @computed('environment.current.canIdentifyByEmail')
  get messagingIntentName() {
    if (this.environment.current.canIdentifyByEmail) {
      return 'message';
    } else {
      return 'campaign';
    }
  }

  @action
  async onSubmit({ website, description, technicalContact }: VerificationRequest) {
    try {
      await this.account.current.verify({ website, description, technicalContact });

      this.analytics.track('onboarding', 'account_verification_submitted');
      this.account.current.set('verificationState', 'pending');
      this.account.current.set('technicalEmail', technicalContact);
    } catch (err) {
      this.alerts.error(
        'account.verification.error',
        'An error occurred. Please contact win@customer.io for help verifying your account.',
      );
    }
  }

  fetchIntegrationStatusTask = restartableTask(async () => {
    return this.api.request('setup_guide/integration_status');
  });
}
